import React from 'react';
import { Box, Typography, Card, CardMedia, CardContent, Chip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { useThemeStore } from '../../stores/themeStore';
import { GRADIENT_ACCENTS, DISPLAY_FONT } from '../../theme';
import type { Outfit } from '../../types';

interface OutfitOfTheDayProps {
  outfit: Outfit;
}

/**
 * OutfitOfTheDay — featured card with special gradient border highlighting
 * the highest-rated outfit from the previous 24 hours.
 */
const OutfitOfTheDay: React.FC<OutfitOfTheDayProps> = ({ outfit }) => {
  const palette = useThemeStore((s) => s.palette);
  const gradient = GRADIENT_ACCENTS[palette].primary;

  return (
    <Box sx={{ mb: 4 }}>
      <Typography variant="h5" component="h2" sx={{ mb: 2, fontWeight: 700 }}>
        ✨ Outfit of the Day
      </Typography>

      <Card
        component={RouterLink}
        to={`/outfit/${outfit.id}`}
        sx={{
          textDecoration: 'none',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          overflow: 'hidden',
          borderRadius: 3,
          boxShadow: `0 0 0 2px transparent`,
          border: '2px solid transparent',
          backgroundImage: `linear-gradient(var(--bg-paper, #1A1A2E), var(--bg-paper, #1A1A2E)), ${gradient}`,
          backgroundOrigin: 'border-box',
          backgroundClip: 'padding-box, border-box',
          '&:hover': {
            transform: 'scale(1.01)',
            transition: 'transform 200ms ease',
          },
          '@media (prefers-reduced-motion: reduce)': {
            '&:hover': { transform: 'none' },
          },
        }}
      >
        <CardMedia
          component="img"
          image={outfit.imageUrl}
          alt={outfit.title || 'Outfit of the Day'}
          sx={{
            width: { xs: '100%', md: 280 },
            height: { xs: 240, md: 'auto' },
            objectFit: 'contain',
            backgroundColor: 'rgba(0, 0, 0, 0.04)',
          }}
        />
        <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', p: 3 }}>
          <Chip
            label="🏆 Outfit of the Day"
            size="small"
            sx={{
              background: gradient,
              color: '#fff',
              fontWeight: 700,
              fontSize: '0.7rem',
              mb: 1.5,
              alignSelf: 'flex-start',
            }}
          />
          <Typography
            variant="h6"
            sx={{ fontFamily: DISPLAY_FONT, fontWeight: 700, color: 'text.primary', mb: 0.5 }}
          >
            {outfit.title || outfit.description || 'Untitled Outfit'}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {outfit.aiAnalysis?.description || outfit.description}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            ⭐ {outfit.ratings?.averageRating?.toFixed(1) || '—'} · {outfit.ratings?.totalRatings || 0} ratings
          </Typography>
        </CardContent>
      </Card>
    </Box>
  );
};

export default OutfitOfTheDay;
